import { useCallback, useState } from "react";
import styled from "styled-components";

import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import TextareaForm from "components/TextareaForm";
import color from "constants/color";
import font from "constants/font";

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
`;

const Label = styled.label`
  font-size: ${font.size14};
  color: ${color.text.black};
  margin-bottom: 8px;
`;

const Required = styled.span`
  margin-left: 4px;
  padding: 2px 6px;
  background-color: ${color.attention};
  color: ${color.white};
  font-size: ${font.size12};
`;

const ButtonWrap = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const Message = styled.p`
  font-size: ${font.size14};
  color: ${color.text.black};
`;

const CANCEL_REASON_MAX_LENGTH = 1000;

const CancelRequestReasonForm = ({
  onSubmit,
  onCancel,
  isLoading = false,
}: {
  onSubmit: (cancelReason: string) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}) => {
  const [cancelReason, setCancelReason] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmitClick = useCallback(() => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setErrorMessage("キャンセル理由を入力してください");
      return;
    }
    if (trimmed.length > CANCEL_REASON_MAX_LENGTH) {
      setErrorMessage(`キャンセル理由は${CANCEL_REASON_MAX_LENGTH}文字以内で入力してください`);
      return;
    }
    setErrorMessage(null);
    setIsConfirmOpen(true);
  }, [cancelReason]);

  const handleConfirmSubmit = useCallback(async () => {
    await onSubmit(cancelReason.trim());
    setIsConfirmOpen(false);
  }, [cancelReason, onSubmit]);

  return (
    <>
      <FormWrap>
        <Label>
          キャンセル理由
          <Required>必須</Required>
        </Label>
        <TextareaForm
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          placeholder="キャンセル理由を入力してください"
          rows={5}
          maxLength={CANCEL_REASON_MAX_LENGTH}
        />
        {errorMessage && <Message style={{ color: color.attention, marginTop: 8 }}>{errorMessage}</Message>}
        <ButtonWrap>
          <Button type="secondary" label="戻る" onClick={onCancel} width="120px" height="44px" disabled={isLoading} />
          <Button
            type="primary"
            label="送信"
            onClick={handleSubmitClick}
            width="120px"
            height="44px"
            disabled={isLoading}
          />
        </ButtonWrap>
      </FormWrap>
      <ConfirmModal
        isVisible={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="キャンセルリクエスト"
        message={<Message>キャンセルリクエストを送信します。よろしいですか？</Message>}
        confirmButtonLabel="送信"
        onClick={handleConfirmSubmit}
      />
    </>
  );
};

export default CancelRequestReasonForm;
